import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DirIcon from '../components/ui/DirIcon';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ScreenLink as Link } from '../components/permissions/ScreenLink';
import { api } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import NewDiscussionModal from '../components/discussions/NewDiscussionModal';
import { canOpenDiscussion, conversationPageParams } from '../utils/discussionParticipants';
import { useAuthStore } from '../store/authStore';
import { getStatusLabel } from '../types';
import type { ConversationSummary, ConversationGroup } from '../types';
import TicketChatView from '../components/TicketChatView';
import { EmployeesList, CustomersList, useOpenDirectChat } from '../components/discussions/ChatPeopleLists';
import { CHAT_TABS, chatsForTab, isDirectChat, parseChatTab, typingText, unreadChatsCount, type ChatTab } from '../utils/chatScreenModel';
import DirectThreadsBar from '../components/discussions/DirectThreadsBar';
import { HomeFilterChip } from '../components/home/HomeFilterChip';
import StateBlock from '../components/ui/StateBlock';
import { getInitials, getAvatarColor } from '../hooks/useLiveChatNotifications';
import {
  MessageCircle, Search, ExternalLink, MessageSquare,
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { he } from 'date-fns/locale';

type ConvFilter = 'all' | 'unread' | 'discussion' | 'tickets' | 'awaiting_reply';
/** P-45b (D-210) — the type chips: all / conversations / discussions (`?kind=`). */
type KindFilter = 'all' | 'conversation' | 'discussion';
const parseKind = (v: string | null): KindFilter => (v === 'conversation' || v === 'discussion' ? v : 'all');

function toTs(v: string | null | undefined): number {
  if (!v) return 0;
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
}

function latestTicketInGroup(group: ConversationGroup) {
  return group.tickets.reduce((best, tk) =>
    (toTs(tk.last_comment_at) > toTs(best.last_comment_at) ? tk : best),
  group.tickets[0]);
}

function formatConvTime(dateStr: string | null, t: (k: string) => string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, 'HH:mm', { locale: he });
  if (isYesterday(d)) return t('chat.yesterday');
  return format(d, 'dd/MM/yyyy', { locale: he }); // DQ-450: the site-wide date format
}

function previewText(text: string | null, max = 60): string {
  if (!text) return '';
  // Replace inline ticket-mention tokens with a friendlier "#title" preview
  const stripped = text.replace(/@@ticket:[0-9a-fA-F-]{36}\|([^@]*?)@@/g, (_full, title) => `#${title || ''}`);
  const clean = stripped.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}

function ConversationRow({
  conv,
  active,
  onClick,
  t,
  showOrg,
}: {
  conv: ConversationSummary;
  active: boolean;
  onClick: () => void;
  t: (k: string) => string;
  showOrg: boolean;
}) {
  // For discussions, the chat name should be the discussion title (per Phase K3).
  // For regular tickets the existing behavior — customer name as the chat name.
  const isDiscussion = conv.task_type === 'discussion';
  const displayName = isDiscussion
    ? (conv.title || conv.customer_name || t('discussions.chat'))
    : (conv.customer_name || conv.title);
  const subline = isDiscussion ? conv.customer_name : null;
  const hasUnread = (conv.unread_count ?? 0) > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-3 text-start transition-colors border-b border-surface-200/80 ${
        active ? 'bg-primary-50' : 'hover:bg-surface-50'
      }`}
    >
      <div className="relative flex-shrink-0">
        <div
          className={`w-12 h-12 rounded-full ${getAvatarColor(displayName)} text-white font-semibold flex items-center justify-center text-sm`}
        >
          {conv.task_type === 'discussion' ? (
            <MessageCircle size={20} />
          ) : (
            getInitials(displayName)
          )}
        </div>
        {hasUnread && (
          <span className="absolute -top-0.5 -end-0.5 min-w-5 h-5 px-1 bg-solid-success text-white text-xs font-semibold tabular-nums rounded-full flex items-center justify-center ring-2 ring-surface-0">
            {conv.unread_count > 9 ? '9+' : conv.unread_count}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${hasUnread ? 'font-semibold text-surface-900' : 'font-medium text-surface-800'}`}>
            {displayName}
          </span>
          {isDiscussion && <KindTag isConversation={conv.is_conversation === true} />}
          <span className="text-xs text-surface-500 flex-shrink-0">
            {formatConvTime(conv.last_comment_at, t)}
          </span>
        </div>
        {subline && (
          <p className="text-xs text-surface-500 truncate">{subline}</p>
        )}
        {showOrg && conv.organization_name && (
          <p className="text-xs text-surface-500 truncate">{conv.organization_name}</p>
        )}
        <p className={`text-xs truncate mt-0.5 ${hasUnread ? 'text-surface-700 font-medium' : 'text-surface-500'}`}>
          {(conv.last_comment_by || conv.last_comment) && (
            <span className="text-surface-500">{conv.last_comment_by || t('chat.deletedEmployee')}: </span>
          )}
          {previewText(conv.last_comment) || t('conversations.noPreview')}
        </p>
      </div>
    </button>
  );
}

/** P-45b — the small "שיחה" / "דיון" tag. */
function KindTag({ isConversation }: { isConversation: boolean }) {
  const { t } = useTranslation();
  return (
    <span className="flex-shrink-0 rounded-full border border-surface-300/60 px-1.5 text-xs text-surface-600" data-testid="kind-tag">
      {t(isConversation ? 'discussions.tagConversation' : 'discussions.tagDiscussion')}
    </span>
  );
}

function CustomerGroupRow({
  group,
  selectedId,
  onSelect,
  t,
  showOrg,
  memberCount,
}: {
  group: ConversationGroup;
  selectedId: string;
  onSelect: (group: ConversationGroup) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  showOrg: boolean;
  /** FIX-CHAT-THREADS-GROUPS: the "קבוצות" tab shows how many members a group has. */
  memberCount?: number;
}) {
  const latest = latestTicketInGroup(group);
  const fallbackName = group.display_name || group.customer_name || t('conversations.unknownCustomer');
  const title = latest?.task_type === 'discussion'
    ? (latest.title || fallbackName)
    : fallbackName;
  const hasUnread = group.total_unread > 0;
  const isActive = group.tickets.some((tk) => tk.id === selectedId);

  return (
    <button
      type="button"
      onClick={() => onSelect(group)}
      className={`w-full flex items-center gap-3 px-3 py-3 text-start transition-colors border-b border-surface-200/80 ${
        isActive ? 'bg-primary-50' : 'hover:bg-surface-50'
      }`}
    >
      <div className="relative flex-shrink-0">
        <div className={`w-12 h-12 rounded-full ${getAvatarColor(title)} text-white font-semibold flex items-center justify-center text-sm`}>
          {latest?.task_type === 'discussion' ? (
            <MessageCircle size={20} />
          ) : (
            getInitials(title)
          )}
        </div>
        {hasUnread && (
          <span className="absolute -top-0.5 -end-0.5 min-w-5 h-5 px-1 bg-solid-success text-white text-xs font-semibold tabular-nums rounded-full flex items-center justify-center ring-2 ring-surface-0">
            {group.total_unread > 9 ? '9+' : group.total_unread}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${hasUnread ? 'font-semibold text-surface-900' : 'font-medium text-surface-800'}`}>
            {title}
          </span>
          {latest?.task_type === 'discussion' && <KindTag isConversation={latest.is_conversation === true} />}
          <span className="text-xs text-surface-500 flex-shrink-0">
            {formatConvTime(latest?.last_comment_at ?? null, t)}
          </span>
        </div>
        {showOrg && group.organization_name && (
          <p className="text-xs text-surface-500 truncate">{group.organization_name}</p>
        )}
        {memberCount != null && (
          <p className="text-xs text-surface-500 truncate" data-testid="group-member-count">{t('chatScreen.groupMembers', { count: memberCount })}</p>
        )}
        <p className={`text-xs truncate mt-0.5 ${hasUnread ? 'text-surface-700 font-medium' : 'text-surface-500'}`}>
          {(latest?.last_comment_by || latest?.last_comment) && (
            <span className="text-surface-500">{latest.last_comment_by || t('chat.deletedEmployee')}: </span>
          )}
          {previewText(latest?.last_comment ?? null) || t('conversations.noPreview')}
        </p>
      </div>
    </button>
  );
}

interface Participant {
  id: string;
  name: string;
  role: 'admin' | 'agent' | 'customer_manager' | 'customer_viewer';
  is_primary?: boolean;
}

function ChatParticipantsBar({
  ticket,
  t,
}: {
  ticket: {
    customer_id?: string;
    customer_name?: string | null;
    extra_customers?: { id: string; name: string; role: Participant['role'] }[];
    agents?: { id: string; name: string; role: Participant['role'] }[];
  };
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const participants: Participant[] = [];
  if (ticket.customer_id && ticket.customer_name) {
    participants.push({
      id: ticket.customer_id,
      name: ticket.customer_name,
      role: 'customer_manager',
      is_primary: true,
    });
  }
  for (const c of ticket.extra_customers ?? []) {
    if (!participants.find((p) => p.id === c.id)) {
      participants.push({ id: c.id, name: c.name, role: c.role });
    }
  }
  for (const a of ticket.agents ?? []) {
    if (!participants.find((p) => p.id === a.id)) {
      participants.push({ id: a.id, name: a.name, role: a.role });
    }
  }
  if (participants.length === 0) return null;

  // Compact avatar-only row: each participant is a 22px circle with a tooltip
  // showing the full name. Staff get a thin primary ring, customers an amber
  // ring, so you can read role at a glance without text labels. This fits 8+
  // participants in a typical chat header without horizontal scrolling.
  const VISIBLE = 8;
  const overflow = Math.max(0, participants.length - VISIBLE);
  const visible = overflow > 0 ? participants.slice(0, VISIBLE) : participants;
  const hiddenNames = overflow > 0 ? participants.slice(VISIBLE).map((p) => p.name).join(', ') : '';

  return (
    <div
      className="flex items-center gap-1 flex-wrap"
      title={t('discussions.participantsTitle')}
    >
      {visible.map((p) => {
        const isStaff = p.role === 'admin' || p.role === 'agent';
        return (
          <span
            key={p.id}
            className={`relative inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold text-white shadow-card ring-2 flex-shrink-0 ${
              isStaff ? 'ring-primary-300' : 'ring-warning-300'
            } ${getAvatarColor(p.name)}`}
            title={`${p.name}${p.is_primary ? ' ★' : ''}`}
            aria-label={p.name}
          >
            {getInitials(p.name)}
          </span>
        );
      })}
      {overflow > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full text-xs font-semibold text-surface-700 bg-surface-200 border border-surface-300 flex-shrink-0"
          title={hiddenNames}
          aria-label={t('conversations.moreParticipants', { count: overflow })}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

/**
 * P-19a item 8 — D-146 (Q-95): "דיונים" — organizational discussions (group
 * chats of 3+ participants, possibly with the customer), on their own page.
 * It is the former "שיחות" screen limited to discussions (the same grouped
 * list, search, unread badges and chat); service tickets live in "קריאות".
 */
export default function DiscussionsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  // P-36 (D-193): a customer login and the internal-only employee do not open one.
  const { internalOnly } = usePermissions();
  const canOpen = canOpenDiscussion(user, internalOnly);
  const [showNew, setShowNew] = useState(false);
  const selectedId = searchParams.get('ticket') || '';
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // `?filter=unread|awaiting_reply&from=home` (Home "ממתין לך", D3): start on that
  // filter and show the removable "מסונן מדף הבית" chip. Read once on mount,
  // because opening a conversation rewrites the URL.
  // D-146 — this page lists discussions only.
  const [filter, setFilter] = useState<ConvFilter>('discussion');
  const kind = parseKind(searchParams.get('kind'));
  const kindParam = kind === 'all' ? undefined : kind;
  // D-326: the tabs (`?tab=`) replace the type chips.
  const tab: ChatTab = parseChatTab(searchParams.get('tab'));
  const setTab = (k: ChatTab) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (k === 'recent') next.delete('tab'); else next.set('tab', k);
    return next;
  }, { replace: true });
  const [fromHome, setFromHome] = useState(() => searchParams.get('from') === 'home');
  const homeChip = fromHome && (filter === 'unread' || filter === 'awaiting_reply')
    ? (filter === 'unread' ? 'unread' : 'awaiting')
    : null;
  const [mobileShowChat, setMobileShowChat] = useState(false);
  // By-customer grouping is now the only mode — the user requested removing
  // the toggle because grouped view is the expected default. We keep the
  // variable to avoid touching every reference in the file.
  const groupByCustomer = true;

  const isAdmin = user?.role === 'admin';

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['conversations', filter, searchQuery, kind],
    queryFn: () => api.getConversations({ filter, kind: kindParam, search: searchQuery || undefined, limit: 80 }),
    enabled: !groupByCustomer,
  });

  const { data: groupedData, isLoading: groupedLoading, error: groupedError, refetch: refetchGrouped, isFetching: groupedFetching } = useQuery({
    queryKey: ['conversations-grouped', filter, searchQuery, kind],
    queryFn: () => api.getConversationsGrouped({ filter, kind: kindParam, search: searchQuery || undefined }),
    enabled: groupByCustomer,
  });

  const conversations: ConversationSummary[] = data?.data ?? [];
  const groups: ConversationGroup[] = groupedData?.data ?? [];
  const meId = user?.id;
  const shownGroups = useMemo(() => chatsForTab(groups, tab, meId), [groups, tab, meId]);
  const unreadChats = unreadChatsCount(groups);

  // D-326: heartbeat + who is online (OTE side), every 25 s while the page is open.
  const { data: presenceData } = useQuery({
    queryKey: ['chat-presence'],
    queryFn: () => api.chatPresence(),
    refetchInterval: 25_000,
    enabled: canOpen,
  });
  const onlineIds = useMemo(() => new Set<string>(presenceData?.data?.online ?? []), [presenceData]);

  const { data: timelineData, isLoading: timelineLoading, error: timelineError, refetch: refetchTimeline, isFetching: timelineFetching } = useQuery({
    queryKey: ['ticket-timeline', selectedId],
    queryFn: () => api.getTicketTimeline(selectedId),
    enabled: !!selectedId,
  });

  const { data: ticketData } = useQuery({
    queryKey: ['ticket', selectedId],
    queryFn: () => api.getTicket(selectedId),
    enabled: !!selectedId,
  });

  const timeline = timelineData?.data?.timeline ?? [];
  const ticket = ticketData?.data;
  // P-45b (QX-215): a customer is in the chat when a customer login takes part (the opener is an employee).
  const hasCustomer = (ticket?.extra_customers?.length ?? 0) > 0
    || ticket?.organization_is_internal === false;
  // Try to find the conv in either data source so header info works in both views
  const selectedConv: Pick<ConversationSummary, 'id' | 'title' | 'task_type' | 'is_conversation' | 'customer_name' | 'status' | 'organization_name'> | undefined = (() => {
    const fromFlat = conversations.find((c) => c.id === selectedId);
    if (fromFlat) return fromFlat;
    for (const g of groups) {
      const tk = g.tickets.find((t) => t.id === selectedId);
      if (tk) {
        return {
          id: tk.id,
          title: tk.title,
          task_type: tk.task_type,
          is_conversation: tk.is_conversation,
          customer_name: tk.customer_name,
          status: tk.status,
          organization_name: g.organization_name,
        };
      }
    }
    return undefined;
  })();

  useEffect(() => {
    if (selectedId) setMobileShowChat(true);
  }, [selectedId]);

  // Optimistic: clear unread badge immediately when opening a conversation
  useEffect(() => {
    if (!selectedId) return;
    queryClient.setQueryData(
      ['conversations', filter, searchQuery, kind],
      (old: { data?: ConversationSummary[]; meta?: unknown } | undefined) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((c) =>
            c.id === selectedId ? { ...c, unread_count: 0 } : c
          ),
        };
      }
    );
    queryClient.setQueryData(
      ['conversations-grouped', filter, searchQuery, kind],
      (old: { data?: ConversationGroup[]; meta?: unknown } | undefined) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((g) => {
            const had = g.tickets.find((t) => t.id === selectedId);
            if (!had) return g;
            return {
              ...g,
              total_unread: Math.max(0, g.total_unread - (had.unread_count ?? 0)),
              tickets: g.tickets.map((t) =>
                t.id === selectedId ? { ...t, unread_count: 0 } : t
              ),
            };
          }),
        };
      }
    );
  }, [selectedId, filter, searchQuery, kind, queryClient]);

  // D-326: opening a chat marks it read - the menu counter follows.
  useEffect(() => {
    if (!selectedId) return undefined;
    const timer = setTimeout(() => { void queryClient.invalidateQueries({ queryKey: ['chat-unread-count'] }); }, 1500);
    return () => clearTimeout(timer);
  }, [selectedId, queryClient]);

  // D-146 — one kind here (discussions): no type chips.
  const filters: { key: ConvFilter; label: string }[] = [];

  const selectConversation = (id: string) => {
    // FIX-B23 (QX-340): the chosen chip (?kind=) stays.
    setSearchParams((prev) => conversationPageParams(prev, id), { replace: true });
    setMobileShowChat(true);
  };

  // D-326: a click on a person opens the 1:1 chat at once (the existing one, else a new one).
  const direct = useOpenDirectChat((id) => {
    setSearchQuery('');
    setSearchInput('');
    setSearchParams((prev) => {
      const next = conversationPageParams(prev, id);
      const tb = prev.get('tab');
      if (tb) next.set('tab', tb);
      return next;
    }, { replace: true });
    setMobileShowChat(true);
  });

  // D-326: "X מקליד/ה..." - poll the chat's typers; ping while I type (throttled).
  const { data: typingData } = useQuery({
    queryKey: ['chat-typing', selectedId],
    queryFn: () => api.getChatTyping(selectedId),
    enabled: !!selectedId,
    refetchInterval: 3_000,
  });
  const typers = typingData?.data?.names ?? [];
  const lastPing = useRef(0);
  const pingTyping = (e: React.FormEvent) => {
    const el = e.target as HTMLElement;
    if (!selectedId || (el.tagName !== 'TEXTAREA' && el.tagName !== 'INPUT')) return;
    const now = Date.now();
    if (now - lastPing.current < 3_000) return;
    lastPing.current = now;
    void api.chatTypingPing(selectedId).catch(() => undefined);
  };

  const selectGroup = (group: ConversationGroup) => {
    const latest = latestTicketInGroup(group);
    if (latest) selectConversation(latest.id);
  };

  const activeGroup = groups.find((g) => g.tickets.some((tk) => tk.id === selectedId));

  const backToList = () => {
    setMobileShowChat(false);
    setSearchParams((prev) => conversationPageParams(prev, null), { replace: true });
  };

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
  };

  // Debounced search: triggers query 350ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // C-05 (clarity, A-08): the empty list's one action.
  const newConversationAction = canOpen ? (
    <button type="button" onClick={() => setShowNew(true)} className="btn btn-secondary btn-sm">{t('discussions.new')}</button>
  ) : undefined;

  const listPanel = (
    <div className="flex flex-col h-full bg-surface-0 border-e border-surface-300/60">
      <div className="p-3 border-b border-surface-300/60 bg-surface-sunken">
        {/* C-10 (clarity, A-31, B-04): the h1 is "שיחות" alone; "שיחה חדשה" sits beside it (D-16: not covered by the floating "+", which is hidden here). */}
        <div className="mb-3 flex items-center gap-2">
          <h1 className="flex items-center gap-2 text-lg font-semibold text-surface-900">
            <MessageSquare size={20} className="text-accent-text" aria-hidden="true" />
            {t('discussions.title')}
          </h1>
          {canOpen && (
            <button type="button" onClick={() => setShowNew(true)} className="btn btn-primary btn-sm ms-auto" data-testid="new-group">{t('chatScreen.newGroup')}</button>
          )}
        </div>
        <div className="relative">
          <Search size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-surface-500" aria-hidden="true" />
          <input
            type="search"
            className="input w-full ps-9 py-2 text-sm bg-surface-0"
            aria-label={t('discussions.searchLabel')}
            placeholder={t('discussions.searchPlaceholderAll')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        {homeChip && (
          <HomeFilterChip
            className="mt-2"
            label={t(`home.filtered.${homeChip}`)}
            onClear={() => { setFromHome(false); setFilter('all'); }}
          />
        )}
        <div className="flex gap-1 mt-2 overflow-x-auto scrollbar-thin" role="tablist" aria-label={t('chatScreen.tabsLabel')} data-testid="chat-tabs">
          {CHAT_TABS.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              data-testid={`chat-tab-${k}`}
              onClick={() => setTab(k)}
              className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                tab === k
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-0 text-surface-600 border border-surface-300/60 hover:bg-surface-50'
              }`}
            >
              {t(`chatScreen.tab.${k}`)}
              {k === 'recent' && unreadChats > 0 && (
                <span className="min-w-4 h-4 px-1 rounded-full bg-solid-success text-white text-xs leading-4 tabular-nums" data-testid="unread-chats-count" aria-label={t('chatScreen.unreadChats', { count: unreadChats })}>{unreadChats}</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex gap-1 mt-2 overflow-x-auto scrollbar-thin">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => { setFromHome(false); setFilter(f.key); }}
              className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === f.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-0 text-surface-600 border border-surface-300/60 hover:bg-surface-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {direct.error && <p role="alert" className="px-3 py-2 text-xs text-danger-text">{direct.error}</p>}
        {tab === 'employees' ? (
          <EmployeesList groups={groups} onlineIds={onlineIds} search={searchQuery} onOpen={(p, k) => void direct.open(p, k)} busyId={direct.busyId} />
        ) : tab === 'customers' ? (
          <CustomersList groups={groups} search={searchQuery} onOpen={(p, k) => void direct.open(p, k)} busyId={direct.busyId} />
        ) : !groupByCustomer ? (
          <>
            {isLoading && <StateBlock kind="loading" rows={6} />}
            {error && !data && (
              <StateBlock kind="error" title={t('states.discussions.listError')} error={error} onRetry={() => void refetch()} retrying={isFetching} />
            )}
            {error && !!data && (
              <StateBlock kind="error" size="inline" title={t('states.refreshFailed')} description={null} onRetry={() => void refetch()} retrying={isFetching} className="border-b border-border px-4 py-2" />
            )}
            {!isLoading && !error && conversations.length === 0 && (
              <StateBlock kind="empty" icon={MessageCircle} title={t('discussions.empty')} description={t('discussions.emptyHint')} action={newConversationAction} />
            )}
            {conversations.map((conv) => (
              <ConversationRow
                key={conv.id}
                conv={conv}
                active={conv.id === selectedId}
                onClick={() => selectConversation(conv.id)}
                t={t}
                showOrg={!!isAdmin}
              />
            ))}
          </>
        ) : (
          <>
            {groupedLoading && <StateBlock kind="loading" rows={6} />}
            {groupedError && !groupedData && (
              <StateBlock kind="error" title={t('states.discussions.listError')} error={groupedError} onRetry={() => void refetchGrouped()} retrying={groupedFetching} />
            )}
            {groupedError && !!groupedData && (
              <StateBlock kind="error" size="inline" title={t('states.refreshFailed')} description={null} onRetry={() => void refetchGrouped()} retrying={groupedFetching} className="border-b border-border px-4 py-2" />
            )}
            {!groupedLoading && !groupedError && groups.length === 0 && (
              <StateBlock kind="empty" icon={MessageCircle} title={t('discussions.empty')} description={t('discussions.emptyHint')} action={newConversationAction} />
            )}
            {!groupedLoading && !groupedError && groups.length > 0 && shownGroups.length === 0 && (
              <StateBlock kind="empty" icon={MessageCircle} title={t('chatScreen.noChats')} description={null} />
            )}
            {shownGroups.map((g) => (
              <CustomerGroupRow
                key={g.group_key}
                group={g}
                selectedId={selectedId}
                onSelect={selectGroup}
                t={t}
                showOrg={!!isAdmin}
                memberCount={tab === 'groups' ? g.participants.length : undefined}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );

  const chatPanel = (
    <div className="flex flex-col h-full flex-1 min-w-0 bg-surface-50">
      {!selectedId ? (
        <div className="hidden md:flex flex-1 flex-col items-center justify-center text-center px-8">
          {/* Empty state per DESIGN_SYSTEM §14: a 40 px glyph, no disc. */}
          <MessageSquare size={40} strokeWidth={1.5} className="mb-3 text-text-disabled" aria-hidden="true" />
          <h2 className="text-base font-semibold text-text">{t('discussions.selectChat')}</h2>
          <p className="text-text-2 text-sm mt-1 max-w-sm">{t('discussions.selectChatHint')}</p>
        </div>
      ) : (
        <>
          <div className="flex-shrink-0 sticky top-16 md:top-0 z-20 flex items-center gap-2 px-3 py-2 bg-surface-0 border-b border-surface-300/60 shadow-card">
            <button
              type="button"
              className="md:hidden p-2 -ms-1 text-surface-600 flex-shrink-0"
              onClick={backToList}
              aria-label={t('shell.back.toDiscussionList')}
            >
              <DirIcon name="back" size={20} />
            </button>
            <div className="flex-1 min-w-0 text-start">
              {(() => {
                const isDiscussion = (selectedConv?.task_type ?? ticket?.task_type) === 'discussion';
                const groupTitle = activeGroup?.display_name || activeGroup?.customer_name;
                const primary = isDiscussion
                  ? (ticket?.title || selectedConv?.title || t('discussions.chat'))
                  : (groupTitle || selectedConv?.customer_name || ticket?.title || t('discussions.chat'));
                const secondary = isDiscussion ? null : (ticket?.title || null);
                return (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-surface-900 truncate">{primary}</p>
                      {ticket?.status && (
                        <span className="text-xs text-surface-500 flex-shrink-0">
                          {/* DQ-089: a discussion / conversation reads "פתוח" / "סגור" (D-154), not the task's "בביצוע". */}
                          {getStatusLabel(ticket.status, t, ticket.task_type ?? selectedConv?.task_type)}
                        </span>
                      )}
                    </div>
                    {typers.length > 0 && (
                      <p className="text-xs text-accent-text" data-testid="typing-line" aria-live="polite">{typingText(typers, t)}</p>
                    )}
                    {/* FIX-CHAT-THREADS-GROUPS: all the chats with this person + "שיחה חדשה" (a separate one). */}
                    {canOpen && meId && activeGroup && isDirectChat(activeGroup) && (
                      <DirectThreadsBar group={activeGroup} meId={meId} selectedId={selectedId} onSelect={selectConversation} />
                    )}
                    {!isDiscussion && secondary && (
                      <p className="text-xs text-surface-500 truncate">{secondary}</p>
                    )}
                    {ticket && (
                      <div className="mt-1.5">
                        <ChatParticipantsBar ticket={ticket} t={t} />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <Link
              to={`/tickets/${selectedId}?chat=1`}
              closed="hide"
              className="p-2 text-surface-500 hover:text-accent-text rounded-lg hover:bg-surface-0/80 flex-shrink-0"
              title={t('discussions.openFull')}
              aria-label={t('discussions.openFull')}
            >
              <ExternalLink size={18} />
            </Link>
          </div>
          <div className="flex-1 min-h-0 flex flex-col" onInput={pingTyping}>
            {timelineLoading && !timeline.length ? (
              <StateBlock kind="loading" rows={5} className="flex-1" />
            ) : timelineError && !timelineData ? (
              <div className="flex-1 flex items-center justify-center">
                <StateBlock
                  kind="error"
                  title={t('states.discussions.threadError')}
                  error={timelineError}
                  onRetry={() => void refetchTimeline()}
                  retrying={timelineFetching}
                  className="mx-4 rounded-xl bg-surface"
                />
              </div>
            ) : (
              <TicketChatView
                // FIX-B38 (QX-840): a fresh view per conversation (scroll position, "הודעה חדשה ↓", draft state).
                key={selectedId}
                timeline={timeline}
                ticketId={selectedId}
                mentionScope="chat_group"
                // P-37 (D-194 Q-178) — the "טיימר מענה" box: offered (unchecked) in a discussion.
                taskType={selectedConv?.task_type}
                isConversation={(selectedConv?.is_conversation ?? ticket?.is_conversation) === true}
                hasCustomer={hasCustomer}
                className="flex-1 min-h-0"
              />
            )}
          </div>
        </>
      )}
    </div>
  );

  // D-326 round 2: the chat box always fits the screen - the typing box is visible without scrolling the page.
  // Height = what is left under the shell's top edge (the bars above vary), minus the mobile bottom bar.
  const shellRef = useRef<HTMLDivElement>(null);
  const [shellStyle, setShellStyle] = useState<React.CSSProperties | undefined>(undefined);
  useLayoutEffect(() => {
    const fit = () => {
      const el = shellRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const bottomBar = document.querySelector<HTMLElement>('nav.fixed.bottom-0');
      const reserve = (bottomBar?.offsetHeight ?? 0) + 8;
      const main = document.getElementById('main');
      const mainPb = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const h = Math.max(320, Math.floor(window.innerHeight - top - reserve));
      setShellStyle({ height: h, marginBottom: -mainPb });
    };
    fit();
    window.addEventListener('resize', fit);
    const t = window.setTimeout(fit, 300); // late bars (clock-in nudge, extension notice) shift the top edge
    return () => { window.removeEventListener('resize', fit); window.clearTimeout(t); };
  }, []);

  return (
    <div
      ref={shellRef}
      style={shellStyle}
      data-testid="chat-shell"
      className="h-[calc(100dvh-10rem)] flex overflow-hidden rounded-lg border border-surface-300/60 shadow-card bg-surface-0"
    >
      {/* List — full width on mobile when no chat selected */}
      <div
        className={`${
          mobileShowChat ? 'hidden md:flex' : 'flex'
        } w-full md:w-[380px] lg:w-[400px] flex-shrink-0 flex-col h-full`}
      >
        {listPanel}
      </div>

      {/* Chat panel */}
      <div
        className={`${
          mobileShowChat ? 'flex' : 'hidden md:flex'
        } flex-1 flex-col h-full min-w-0`}
      >
        {chatPanel}
      </div>

      {showNew && (
        <NewDiscussionModal
          groupWording
          onClose={() => setShowNew(false)}
          onCreated={(created) => {
            // D-193: straight into the new discussion.
            setShowNew(false);
            setFilter('discussion');
            setSearchQuery('');
            setSearchInput('');
            selectConversation(created.id);
          }}
          onOpenChat={(id) => {
            // FIX-CHAT-THREADS-GROUPS: one other person - straight into the direct chat with them.
            setShowNew(false);
            setFilter('discussion');
            setSearchQuery('');
            setSearchInput('');
            selectConversation(id);
          }}
        />
      )}
    </div>
  );
}
